/**
 * "Review this prompt", in full screen: the person's prompt on one side; on the other, what is
 * wrong and what could be better, and a better prompt — the basic checks at once (free, no AI) and,
 * on plans with analysis, the plugged-in AI (or a teammate's, as a peer review) scoring it and
 * rewriting it.
 *
 * Three ways out, always the same: Cancel (back to the prompt, nothing applied), discard the
 * suggestion and apply the original (the prompt as it was before the review), or apply the
 * suggestion (as shown — the person may edit it first). What "apply" means is the caller's: send it,
 * in the prompt box; take it as the new text, on the Prompts screen.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertTriangle, Check, CheckCircle2, Loader2, Sparkles, Undo2, X } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { readReview, scorePrompt } from '../lib/promptScore'
import { useOrderResult } from '../lib/useOrderResult'
import { ErrorBanner } from './primitives'

export function PromptReviewDialog({
  prompt,
  onApplySuggestion,
  onApplyOriginal,
  onClose,
  autoRun = false,
}: {
  prompt: string
  /** The suggested prompt, as the person left it. */
  onApplySuggestion: (improved: string) => void
  /** The prompt as it was before the review. */
  onApplyOriginal: (original: string) => void
  /** Cancel: back to the prompt, nothing applied. */
  onClose: () => void
  /** Start the AI review at once (review before sending), when the plan includes it. */
  autoRun?: boolean
}) {
  const { t } = useI18n()
  const basic = useMemo(() => scorePrompt(prompt), [prompt])
  const [reviewer, setReviewer] = useState('')
  const [orderId, setOrderId] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [suggestion, setSuggestion] = useState<string | null>(null)

  const { data: plan } = useQuery({ queryKey: ['billing'], queryFn: api.billing.state, retry: false })
  const { data: mates } = useQuery({ queryKey: ['teammates'], queryFn: () => api.workOrders.teammates(), retry: false })
  const peers = (mates ?? []).filter((m) => !m.isMe && m.acceptsTeamWork && (m.remoteOn || m.desktopOn))
  const analysis = plan?.entitlements.promptAnalysis ?? false

  const ask = useMutation({
    mutationFn: () => api.analysis.reviewPrompt({ prompt, reviewerDeveloperId: reviewer || null }),
    onSuccess: (order) => setOrderId(order.id),
    onError: (error) =>
      setProblem(error instanceof ApiError && error.code && ['remote_off', 'plan_limit', 'plan_team_only', 'teammate_not_accepting', 'teammate_remote_off', 'not_in_team'].includes(error.code)
        ? t(`review.error.${error.code}`)
        : t('error.generic')),
  })
  const started = useRef(false)
  useEffect(() => {
    if (autoRun && analysis && !started.current) {
      started.current = true
      ask.mutate()
    }
  }, [autoRun, analysis, ask])
  const { order, finished } = useOrderResult(orderId)
  const review = readReview(order?.lastReply)
  // The suggestion arrives once; from then on it is the person's to edit.
  useEffect(() => {
    if (review && suggestion === null) setSuggestion(review.improved)
  }, [review, suggestion])

  // Escape is Cancel, as everywhere.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const missing = basic.checks.filter((c) => !c.ok)

  return (
    <div className="review-full" role="dialog" aria-modal="true" aria-label={t('review.title')}>
      <header className="review-full__head">
        <Sparkles size={16} />
        <strong>{t('review.title')}</strong>
        <span className={`review-full__score review-full__score--${basic.score >= 75 ? 'good' : basic.score >= 45 ? 'ok' : 'weak'}`} title={t('review.basic')}>
          {basic.score}
        </span>
        {review && <span className="small muted">{t('review.aiScore', { score: review.score })}</span>}
        <span className="spacer" />
        <button type="button" className="btn btn--ghost btn--icon btn--sm" onClick={onClose} aria-label={t('review.cancel')} title={t('review.cancel')}>
          <X size={16} />
        </button>
      </header>

      <div className="review-full__body">
        <section className="review-full__col">
          <h3 className="review-full__label">{t('review.original')}</h3>
          <pre className="review-full__text">{prompt}</pre>
          <h3 className="review-full__label">{t('review.basic')}</h3>
          <ul className="review-checks">
            {basic.checks.map((check) => (
              <li key={check.key} className={check.ok ? 'is-ok' : 'is-missing'}>
                {check.ok ? <Check size={12} /> : <X size={12} />} {t(`review.check.${check.key}`)}
              </li>
            ))}
          </ul>
        </section>

        <section className="review-full__col">
          <h3 className="review-full__label">{t('review.whatToImprove')}</h3>
          {missing.length === 0 && !review && <p className="small muted" style={{ margin: 0 }}>{t('review.basicAllGood')}</p>}
          {(missing.length > 0 || (review?.issues.length ?? 0) > 0) && (
            <ul className="review-full__issues">
              {(review?.issues ?? []).map((issue, i) => (
                <li key={`ai-${i}`}><AlertTriangle size={13} /> {issue}</li>
              ))}
              {missing.map((check) => (
                <li key={check.key}><AlertTriangle size={13} /> {t(`review.fix.${check.key}`)}</li>
              ))}
            </ul>
          )}

          <h3 className="review-full__label">{t('review.suggestion')}</h3>
          {!analysis ? (
            <p className="small muted" style={{ margin: 0 }}>
              {t('review.proOnly')} <Link to="/settings/billing" onClick={onClose}>{t('plans.upgrade')}</Link>
            </p>
          ) : !orderId ? (
            <div className="row row--wrap">
              <select className="select" style={{ width: 'auto' }} value={reviewer} onChange={(e) => setReviewer(e.target.value)} aria-label={t('review.who')}>
                <option value="">{t('review.myAi')}</option>
                {peers.map((m) => (
                  <option key={m.developerId} value={m.developerId}>{t('review.peer', { name: m.name })}</option>
                ))}
              </select>
              <button type="button" className="btn btn--primary btn--sm" onClick={() => ask.mutate()} disabled={ask.isPending}>
                <Sparkles size={13} /> {t('review.run')}
              </button>
            </div>
          ) : !finished ? (
            <span className="small muted row row--tight"><Loader2 size={14} className="spin" /> {t('review.waiting')}</span>
          ) : suggestion !== null ? (
            <textarea className="input review-full__suggestion" value={suggestion} onChange={(e) => setSuggestion(e.target.value)} aria-label={t('review.suggestion')} />
          ) : (
            <p className="small" style={{ margin: 0 }}>{order?.status === 'Done' ? t('review.unreadable') : t(`work.reason.${order?.endReason ?? 'not_picked_up'}`)}</p>
          )}
          {problem && <ErrorBanner message={problem} />}
        </section>
      </div>

      <footer className="review-full__foot">
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          {t('review.cancel')}
        </button>
        <span className="spacer" />
        <button type="button" className="btn" onClick={() => onApplyOriginal(prompt)}>
          <Undo2 size={14} /> {t('review.applyOriginal')}
        </button>
        <button type="button" className="btn btn--primary" onClick={() => suggestion && onApplySuggestion(suggestion)} disabled={!suggestion?.trim()} title={suggestion ? undefined : t('review.noSuggestionYet')}>
          <CheckCircle2 size={14} /> {t('review.applySuggestion')}
        </button>
      </footer>
    </div>
  )
}
